import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ipconfig from "../../Core/ipconfig";
import {
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Chip,
  TableContainer,
  Paper,
  useTheme,
} from "@mui/material";
import Spinner from "../../Shared/Feature/LoadingSpinner/Spinner";

export default function PartnerReferrals({ partnerId }: { partnerId: string }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchReferrals = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${ipconfig}/admin/partner/referrals?partnerId=${partnerId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setData(res.data.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (partnerId) {
      fetchReferrals();
    }
  }, [partnerId]);

  if (loading) {
    return <Spinner height="200px" />;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", height: "100%" }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "text.primary" }}>
        Referred Customers ({data.length})
      </Typography>

      {data.length > 0 ? (
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{
            borderRadius: "12px",
            maxHeight: "350px",
            "&::-webkit-scrollbar": {
              width: "5px",
              height: "5px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "divider",
              borderRadius: "4px",
            },
          }}
        >
          <Table stickyHeader size="medium">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#1e293b" }}>Customer Name</TableCell>
                <TableCell sx={{ fontWeight: 700, backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#1e293b" }}>Email Address</TableCell>
                <TableCell sx={{ fontWeight: 700, backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#1e293b" }}>Payment Status</TableCell>
                <TableCell sx={{ fontWeight: 700, backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#1e293b" }}>Sync Status</TableCell>
                <TableCell sx={{ fontWeight: 700, backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#1e293b" }}>Commission Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((r, idx) => (
                <TableRow
                  key={r.customerId || idx}
                  sx={{
                    "&:hover": {
                      backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#1e293b50",
                    },
                    transition: "all 0.15s ease",
                  }}
                >
                  <TableCell sx={{ fontWeight: 600 }}>{r.customerName}</TableCell>
                  <TableCell
                    onClick={(e) => {
                      e.stopPropagation();
                      if (r.email && r.email !== "—") {
                        navigate("/user", { state: { email: r.email, search: r.email, userId: r.customerId } });
                      }
                    }}
                    sx={{
                      color: r.email && r.email !== "—" ? "primary.main" : "text.primary",
                      cursor: r.email && r.email !== "—" ? "pointer" : "default",
                      fontWeight: r.email && r.email !== "—" ? 600 : 400,
                      "&:hover": r.email && r.email !== "—" ? { textDecoration: "underline" } : {}
                    }}
                  >
                    {r.email}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={r.paymentStatus}
                      color={r.paymentStatus === "Paid" ? "success" : "warning"}
                      size="small"
                      sx={{ fontWeight: 700, fontSize: "11px" }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={r.syncStatus}
                      color={r.syncStatus === "Synced" ? "success" : "warning"}
                      size="small"
                      sx={{ fontWeight: 700, fontSize: "11px" }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: "success.main" }}>
                    {r.commissionAmount !== undefined ? `₹${r.commissionAmount}` : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box sx={{ p: 4, textAlign: "center", border: "1px dashed", borderColor: "divider", borderRadius: "12px" }}>
          <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500 }}>
            No referred customers found for this partner.
          </Typography>
        </Box>
      )}
    </Box>
  );
}